import {
  NotificationDTO,
  NotificationType,
  PlanCommentNotificationContext,
  SmartSuggestionNotificationContext,
} from '../dtos/notifications.dto';

export interface NotificationTypeMeta {
  title: string;
  icon: string;
  color: string;
  avatarUrl?: string;
}

export interface NotificationFilter {
  key: NotificationType | 'all';
  label: string;
}

// shared by the notifications bell dropdown and the dedicated notifications page (requirements.md FR-5)
export const NOTIFICATION_TYPE_META: Record<
  NotificationType,
  NotificationTypeMeta
> = {
  [NotificationType.CELEBRATION]: {
    title: 'You have been celebrated!',
    icon: 'star',
    color: '#F59E0B',
  },
  [NotificationType.COMMENT_ON_LOG]: {
    title: 'Session Comment',
    icon: 'card-checklist',
    color: '#3B82F6',
  },
  [NotificationType.COMMENT_ON_PLAN]: {
    title: 'Plan Comment',
    icon: 'compass',
    color: '#8B5CF6',
  },
  [NotificationType.COMMENT_ON_EVIDENCE]: {
    title: 'Evidence Comment',
    icon: 'file-earmark-arrow-up',
    color: '#EC4899',
  },
  [NotificationType.SMART_SUGGESTION]: {
    title: 'Smart Suggestion',
    icon: 'stars',
    color: '#27004b',
    avatarUrl: '/assets/smart-suggestion-avatar.png',
  },
  [NotificationType.YOU_HAVE_BEEN_OBSERVED]: {
    title: 'You have been observed!',
    icon: 'clipboard2-check-fill',
    color: '#d4478a',
    avatarUrl: '/assets/smart-suggestion-avatar.png',
  },
};

export const NOTIFICATION_FILTERS: NotificationFilter[] = [
  { key: 'all', label: 'All' },
  { key: NotificationType.CELEBRATION, label: 'Celebrations' },
  { key: NotificationType.COMMENT_ON_LOG, label: 'Session Comments' },
  { key: NotificationType.COMMENT_ON_PLAN, label: 'Plan Comments' },
  { key: NotificationType.COMMENT_ON_EVIDENCE, label: 'Evidence Comments' },
  { key: NotificationType.SMART_SUGGESTION, label: 'Smart Suggestions' },
  { key: NotificationType.YOU_HAVE_BEEN_OBSERVED, label: 'Observations' },
];

/**
 * Deep-link to the entity a notification is about. `session-routing` and `evidence-routing`
 * are guard-only routes (RouteGuard) that resolve the entity client-side before redirecting,
 * since a coachlog's type-specific info page and an evidence's owning user aren't known here.
 *
 * COMMENT_ON_PLAN carries `notification_context` (phase/deliverable/action item ids), which
 * `ImplementationPlanDetailsPageComponent` reads back out of the query params to expand the
 * right phase/deliverable and auto-open the corresponding edit modal.
 */
export const notificationLink = (
  notification: NotificationDTO
): string | null => {
  switch (notification.notification_type) {
    case NotificationType.CELEBRATION:
      return `/celebrations/${notification.notification_ref_id}`;
    case NotificationType.COMMENT_ON_LOG:
      // open_comments is forwarded by RouteGuard's session-routing branch onto whichever
      // type-specific info page the log resolves to, which reads it to auto-open its
      // comments panel (not supported on smart-coach logs — no comments panel there)
      return `/session-routing/${notification.notification_ref_id}?open_comments=true`;
    case NotificationType.COMMENT_ON_PLAN: {
      const context =
        notification.notification_context as PlanCommentNotificationContext | null;
      const params = new URLSearchParams();
      // mutually exclusive: ImplementationPlanDetailsPageComponent.handleQueryParams()
      // treats deliverable_id/action_item_id as two independent triggers, so an action
      // item comment must only set action_item_id or both modals open stacked
      if (context?.action_item_id) {
        params.set('action_item_id', String(context.action_item_id));
      } else if (context?.deliverable_id) {
        params.set('deliverable_id', String(context.deliverable_id));
      }
      const query = params.toString();
      return `/plans/implementation-plan/${
        notification.notification_ref_id
      }/details${query ? `?${query}` : ''}`;
    }
    case NotificationType.COMMENT_ON_EVIDENCE:
      return `/evidence-routing/${notification.notification_ref_id}`;
    case NotificationType.YOU_HAVE_BEEN_OBSERVED: {
      const context =
        notification.notification_context as SmartSuggestionNotificationContext | null;
      return context?.action || null;
    }
    case NotificationType.SMART_SUGGESTION: {
      const context =
        notification.notification_context as SmartSuggestionNotificationContext | null;
      const action = context?.action;
      if (!action) return null;
      if (action === '#quickWalkModal') return '/?open=quick_walk';
      if (action === '#celebrationsModal') return '/celebrations';
      return action;
    }
    default:
      return null;
  }
};

/**
 * Heading label for a notification. COMMENT_ON_PLAN covers both deliverable and action item
 * comments under one notification_type, so it's split into a more specific label here using
 * notification_context rather than in NOTIFICATION_TYPE_META (which is keyed by type alone).
 * Uses this plan's own custom name for "deliverable"/"action item" (e.g. "Objective Comment")
 * when the notification carries one, falling back to a generic label otherwise.
 */
export const notificationTitle = (notification: NotificationDTO): string => {
  if (notification.notification_type === NotificationType.COMMENT_ON_PLAN) {
    const context =
      notification.notification_context as PlanCommentNotificationContext | null;
    if (context?.action_item_id) {
      return `${context.displayname ?? 'Action Item'} Comment`;
    }
    if (context?.deliverable_id) {
      return `${context.displayname ?? 'Deliverable'} Comment`;
    }
  }
  return NOTIFICATION_TYPE_META[notification.notification_type]?.title ?? '';
};
